import type { UpdateCustomRoleMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateCustomRoleDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface UpdateCustomRoleInput {
  id: string;
  name: string;
  description?: string | null;
  roleKeys: string[];
  userIds: string[];
}

export const useUpdateCustomRole = createMutationHook({
  name: 'updateCustomRole',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.customRole.customRoleUpdate.mutationOptions({ onSuccess }),
  mapInput: (input: UpdateCustomRoleInput) => input,
  // Invalidate the list family and the per-id detail family the custom-role
  // settings page reads via `useGetCustomRoleById` (tRPC `customRoleById`),
  // so the page reflects the save. The factory's invalidate doesn't see the
  // mutation variables, so the byId family is invalidated as a whole rather
  // than scoped to the saved id.
  invalidate: (trpc) => [
    trpc.frontend.role.getCustomRoles.queryKey(),
    trpc.frontend.customRole.customRoleById.queryKey(),
  ],
  // Maps the tRPC response (camelCase `affectedRows`) back into the Apollo
  // shape (snake_case `affected_rows`) so call sites can keep checking
  // `result.customRoleUpdate?.affected_rows !== 1`.
  mapResponse: (result: {
    affectedRows: number;
  }): UpdateCustomRoleMutation => ({
    customRoleUpdate: {
      __typename: 'GenericMutationOutput',
      affected_rows: result.affectedRows,
    },
  }),
  graphqlDocument: UpdateCustomRoleDocument,
  graphqlVariables: (input: UpdateCustomRoleInput) => ({
    input: {
      Id: input.id,
      Name: input.name,
      Description: input.description ?? undefined,
      RoleKeys: input.roleKeys,
      UserIds: input.userIds,
    },
  }),
  // Evict both the list field and the by-pk field so the detail-page refetch
  // and the settings list pick up the rename.
  evictFields: ['custom_role', 'custom_role_by_pk'],
});
